import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import PropTypes from "prop-types";

const Axes = ({ xScale, yScale, height, width, margin, xTicks, yTicks }) => {
  const ref = useRef();

  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove(); // Clear previous axes/lines

    // Append or update the X axis
    svg
      .append("g")
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${height})`)
      .call(d3.axisBottom(xScale).ticks(xTicks)) // Control the number of ticks on the X axis
      .call((g) =>
        g
          .selectAll(".tick line")
          .clone()
          .attr("y2", -height)
          .attr("stroke-opacity", 0.1)
      ) // Adding grid lines for X axis
      .call((g) => g.selectAll("text").style("font-family", "monospace")); // Customize font

    // Append or update the Y axis
    svg
      .append("g")
      .attr("class", "y-axis")
      .call(d3.axisLeft(yScale).ticks(yTicks)) // Control the number of ticks on the Y axis
      .call((g) =>
        g
          .selectAll(".tick line")
          .clone()
          .attr("x2", width)
          .attr("stroke-opacity", 0.1)
      ) // Adding grid lines for Y axis
      .call((g) => g.selectAll("text").style("font-family", `monospace`)); // Customize font

    // Check if yScale includes 0 and draw a line across the chart at y=0
    if (yScale.domain()[0] <= 0 && yScale.domain()[1] >= 0) {
      svg
        .append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", yScale(0))
        .attr("y2", yScale(0))
        .attr("stroke", "currentColor") // or any color you prefer
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2,2"); // Optional: makes the line dashed
    }

    // Check if xScale includes 0 and draw a line across the chart at x=0
    if (xScale.domain()[0] <= 0 && xScale.domain()[1] >= 0) {
      svg
        .append("line")
        .attr("x1", xScale(0))
        .attr("x2", xScale(0))
        .attr("y1", 0)
        .attr("y2", height)
        .attr("stroke", "currentColor") // or any color you prefer
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "2,2"); // Optional: makes the line dashed
    }
  }, [xScale, yScale, height, width, margin, xTicks, yTicks]);

  return (
    <g ref={ref} transform={`translate(${margin.left},${margin.top})`}></g>
  );
};

Axes.propTypes = {
  xScale: PropTypes.func.isRequired,
  yScale: PropTypes.func.isRequired,
  height: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number
  }).isRequired,
  xTicks: PropTypes.number,
  yTicks: PropTypes.number
};

Axes.defaultProps = {
  margin: { top: 20, right: 30, bottom: 30, left: 40 },
  xTicks: 5, // Default number of ticks for x-axis
  yTicks: 5 // Default number of ticks for y-axis
};

export default Axes;
